import { keyframes } from "@emotion/react";
import { Box } from "@mui/system";

import { useStoreState } from "store/store";
import { palette } from "theme/palette";

// Default text shown when something triggers isGlobalLoading without
// setting a contextual message. Tries to feel less robotic than just
// "Loading…" — but if you see this in the UI, the right fix is to swap
// the calling setIsGlobalLoading(true) for setLoadingMessage("…specific
// thing happening…") at the call site, not to change this default.
const DEFAULT_LOADING_MESSAGE = "Just a moment…";

// Shimmer keyframes — sweeps a bright band across the text horizontally.
// The gradient is wider than the text (200% background-size) and the
// position animates from off-right (200%) to off-left (-200%), so the
// highlight passes through the middle once per cycle. Duration is on
// the slow side (2.4s) — fast shimmer reads as "frantic / broken", slow
// shimmer reads as "working on it / calm".
const shimmer = keyframes({
  "0%": { backgroundPosition: "200% 0" },
  "100%": { backgroundPosition: "-200% 0" },
});

// Soft fade-in so the message doesn't pop in jarringly when loading
// flips on for very short operations (the eye already sees motion from
// the shimmer; the fade just keeps the entrance smooth).
const fadeIn = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
});

export const Loader = () => {
  const isGlobalLoading = useStoreState((s) => s.isGlobalLoading);
  const loadingMessage = useStoreState((s) => s.loadingMessage);

  if (!isGlobalLoading) {
    return null;
  }

  const message = loadingMessage ?? DEFAULT_LOADING_MESSAGE;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: palette.white,
        zIndex: 999,
        // Light fade-in on the whole overlay so the white panel itself
        // doesn't slam in on top of the page content underneath.
        animation: `${fadeIn} 180ms ease-out`,
      }}
    >
      <Box
        // The shimmer is applied to the text via background-clip:text +
        // transparent text-fill. The trick: paint the gradient onto the
        // text-shaped clipping mask, then animate background-position so
        // the highlight band moves across the visible letters.
        //
        // The "dark" stops are `lightGrey` so the text is always
        // legible (palette.lightGrey is mid-contrast on white). The
        // bright middle stop is `black` so the highlight reads as a
        // pulse of full opacity sweeping across.
        sx={{
          fontSize: { xs: 18, sm: 22, md: 24 },
          fontWeight: 500,
          letterSpacing: "0.01em",
          textAlign: "center",
          padding: "0 24px",
          // Keep long messages on one line where possible, but allow wrap
          // on mobile so they don't get cut off.
          maxWidth: "min(640px, 90vw)",
          backgroundImage: `linear-gradient(
            90deg,
            ${palette.lightGrey} 0%,
            ${palette.lightGrey} 35%,
            ${palette.black} 50%,
            ${palette.lightGrey} 65%,
            ${palette.lightGrey} 100%
          )`,
          backgroundSize: "200% 100%",
          backgroundRepeat: "no-repeat",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          // The shimmer animation. `linear` (not ease) so the highlight
          // moves at a steady pace — easing makes the pulse hesitate at
          // each end, which reads as a glitch.
          animation: `${shimmer} 2.4s linear infinite`,
        }}
      >
        {message}
      </Box>
    </Box>
  );
};
